import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { CompanyFilter } from '../components/CompanyFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { MapPin, ArrowRight } from 'lucide-react';

export default function SiteAnalyticsPage() {
  const [data, setData] = useState({ sites: [] });
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filterCompany !== 'all') params.company_id = filterCompany;
    api.get(`/analytics/site-analysis?${new URLSearchParams(params)}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filterCompany]);

  const chartData = (data.sites || []).filter(s => s.cost_per_hour > 0).slice(0, 10).map(s => ({ name: s.name?.substring(0, 18), cph: s.cost_per_hour, spend: s.total_spend }));

  return (
    <div data-testid="site-analytics-page">
      <div className="page-header">
        <div><h1 className="page-title">Site Analytics</h1><p className="text-sm text-slate-500 mt-1">Compare mining sites by spend, performance, and efficiency</p></div>
      </div>

      <div className="flex gap-3 mb-6" data-testid="site-analytics-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} className="w-52" />
      </div>

      {chartData.length > 0 && (
        <Card className="stat-card mb-6" data-testid="site-cph-chart">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Cost Per Hour by Site</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${v}`} /><YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11, fill: '#334155' }} /><Tooltip formatter={v => `₹${Number(v).toFixed(2)}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="cph" fill="#0F172A" radius={[0,4,4,0]} name="Cost/Hr" /></BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machines</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Products</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Change-outs</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            (data.sites || []).length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No site data</TableCell></TableRow> :
            (data.sites || []).map(s => (
              <TableRow key={s.site_id} className="hover:bg-slate-50/50">
                <TableCell className="font-medium text-slate-900"><div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" />{s.name}</div></TableCell>
                <TableCell className="text-sm text-slate-500">{s.location}</TableCell>
                <TableCell className="text-sm font-medium">₹{s.total_spend.toLocaleString()}</TableCell>
                <TableCell className="text-sm font-bold text-slate-900">{s.cost_per_hour > 0 ? `₹${s.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.total_hours.toLocaleString()}h</TableCell>
                <TableCell className="text-sm text-slate-600">{s.machine_count}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.product_count}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.change_outs}</TableCell>
                <TableCell><Link to={`/analytics/sites/${s.site_id}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`drill-site-${s.name}`}>Detail <ArrowRight size={12} /></Link></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
